var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Autocomplete, Chip, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DataTypeOperator } from "../../../models/audience/filter.model";
import { useTranslation } from "react-i18next";
/**
 * A controlled numeric input that accepts negative (and intermediate "-"/empty) input.
 *
 * Rendered as <TextField type="text"> rather than type="number" because the browser reports a
 * lone "-" in a number input as an empty string, which would parse to NaN and wipe the typed sign.
 * We hold local string state so a partially-typed "-" persists, and only sync a parsed number to
 * the parent once the text is a valid finite number (otherwise we emit undefined).
 */
var NumberInput = function (_a) {
    var value = _a.value, onChange = _a.onChange, type = _a.type, autoFocus = _a.autoFocus, sx = _a.sx;
    var _b = useState(value === undefined || value === null || Number.isNaN(value) ? '' : value.toString()), text = _b[0], setText = _b[1];
    // Sync local text from the value prop when the external value changes (operator switch / reset),
    // without clobbering in-progress typing. Only re-sync when the parsed local text no longer
    // matches the incoming value.
    useEffect(function () {
        var parsed = text === '' ? undefined : (type === 'int' ? parseInt(text) : parseFloat(text));
        var normalizedParsed = parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined;
        if (normalizedParsed !== value) {
            setText(value === undefined || value === null || Number.isNaN(value) ? '' : value.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    var pattern = type === 'int' ? /^-?\d*$/ : /^-?\d*\.?\d*$/;
    return (_jsx(TextField, { size: 'small', autoFocus: autoFocus, hiddenLabel: true, variant: 'outlined', type: 'text', inputProps: { inputMode: type === 'int' ? 'numeric' : 'decimal' }, value: text, onChange: function (e) {
            var raw = e.target.value;
            if (!pattern.test(raw)) {
                return;
            }
            setText(raw);
            var parsed = raw === '' ? NaN : (type === 'int' ? parseInt(raw) : parseFloat(raw));
            onChange(Number.isFinite(parsed) ? parsed : undefined);
        }, sx: sx }));
};
export var FilterNumericInput = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var operator = _a.operator, filterRow = _a.filterRow, setFilterRow = _a.setFilterRow, type = _a.type;
    var content = null;
    var t = useTranslation().t;
    switch (operator) {
        case DataTypeOperator.EQUAL:
        case DataTypeOperator.NOT_EQUAL:
        case DataTypeOperator.MORE_THAN:
        case DataTypeOperator.MORE_THAN_EQUAL:
        case DataTypeOperator.LESS_THAN:
        case DataTypeOperator.LESS_THAN_EQUAL:
            content = (_jsx(NumberInput, { type: type, autoFocus: true, value: type === 'int' ? (_b = filterRow.longValues) === null || _b === void 0 ? void 0 : _b[0] : (_c = filterRow.doubleValues) === null || _c === void 0 ? void 0 : _c[0], onChange: function (n) {
                    switch (type) {
                        case "int":
                            setFilterRow(__assign(__assign({}, filterRow), { longValues: [n] }));
                            break;
                        case "decimal":
                            setFilterRow(__assign(__assign({}, filterRow), { doubleValues: [n] }));
                            break;
                    }
                }, sx: { width: '100%', minWidth: 200 } }));
            break;
        case DataTypeOperator.IN:
        case DataTypeOperator.NOT_IN:
            content = (_jsx(Autocomplete, { size: 'small', clearIcon: false, options: [], freeSolo: true, multiple: true, renderTags: function (value, props) {
                    return value.map(function (option, index) { return (_jsx(Chip, __assign({ size: 'small', label: option }, props({ index: index })))); });
                }, renderInput: function (params) { return _jsx(TextField, __assign({}, params, { type: 'number', placeholder: t("Add Values"), hiddenLabel: true })); }, sx: { width: '100%', minWidth: 200 }, value: type === 'int' ? ((_d = filterRow.longValues) === null || _d === void 0 ? void 0 : _d.filter(function (v) { return !!v; })) || [] : ((_e = filterRow.doubleValues) === null || _e === void 0 ? void 0 : _e.filter(function (v) { return !!v; })) || [], onChange: function (e, value) {
                    switch (type) {
                        case "int":
                            setFilterRow(__assign(__assign({}, filterRow), { longValues: value.map(function (v) { return parseInt((v === null || v === void 0 ? void 0 : v.toString()) || ''); }) || [] }));
                            break;
                        case "decimal":
                            setFilterRow(__assign(__assign({}, filterRow), { doubleValues: value.map(function (v) { return parseFloat((v === null || v === void 0 ? void 0 : v.toString()) || ''); }) || [] }));
                            break;
                    }
                } }));
            break;
        case DataTypeOperator.BETWEEN:
        case DataTypeOperator.NOT_BETWEEN:
        case DataTypeOperator.PERIOD_BETWEEN:
        case DataTypeOperator.PERIOD_NOT_BETWEEN:
            content = (_jsxs(Stack, __assign({ direction: 'row', gap: 0.5, alignItems: 'center', sx: { width: '100%', minWidth: 200 } }, { children: [_jsx(NumberInput, { type: type, autoFocus: true, value: type === 'int' ? (_f = filterRow.longValues) === null || _f === void 0 ? void 0 : _f[0] : (_g = filterRow.doubleValues) === null || _g === void 0 ? void 0 : _g[0], onChange: function (n) {
                            var _a, _b;
                            switch (type) {
                                case "int":
                                    setFilterRow(__assign(__assign({}, filterRow), { longValues: [n, (_a = filterRow.longValues) === null || _a === void 0 ? void 0 : _a[1]] }));
                                    break;
                                case "decimal":
                                    setFilterRow(__assign(__assign({}, filterRow), { doubleValues: [n, (_b = filterRow.doubleValues) === null || _b === void 0 ? void 0 : _b[1]] }));
                                    break;
                            }
                        }, sx: { flex: 1 } }), _jsx(Typography, { children: "-" }), _jsx(NumberInput, { type: type, value: type === 'int' ? (_h = filterRow.longValues) === null || _h === void 0 ? void 0 : _h[1] : (_j = filterRow.doubleValues) === null || _j === void 0 ? void 0 : _j[1], onChange: function (n) {
                            var _a, _b;
                            switch (type) {
                                case "int":
                                    setFilterRow(__assign(__assign({}, filterRow), { longValues: [(_a = filterRow.longValues) === null || _a === void 0 ? void 0 : _a[0], n] }));
                                    break;
                                case "decimal":
                                    setFilterRow(__assign(__assign({}, filterRow), { doubleValues: [(_b = filterRow.doubleValues) === null || _b === void 0 ? void 0 : _b[0], n] }));
                                    break;
                            }
                        }, sx: { flex: 1 } })] })));
    }
    return content;
};
